import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Typography,
  Container,
  Paper,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import ContactsIcon from '@mui/icons-material/Contacts';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PestControlIcon from '@mui/icons-material/PestControl';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScienceIcon from '@mui/icons-material/Science';
import ShieldIcon from '@mui/icons-material/Shield';
import StraightenIcon from '@mui/icons-material/Straighten';
import FactoryIcon from '@mui/icons-material/Factory';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import BiotechIcon from '@mui/icons-material/Biotech';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CategoryIcon from '@mui/icons-material/Category';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PublicIcon from '@mui/icons-material/Public';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EventIcon from '@mui/icons-material/Event';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LinkIcon from '@mui/icons-material/Link';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WavesIcon from '@mui/icons-material/Waves';
import GrassIcon from '@mui/icons-material/Grass';
import TableViewIcon from '@mui/icons-material/TableView';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import BusinessManagement from '../components/admin/BusinessManagement';
import ContactManagement from '../components/admin/ContactManagement';
import UserManagement from '../components/admin/UserManagement';
import FtpCredentialManagement from '../components/admin/FtpCredentialManagement';
import DeviceApiKeyManagement from '../components/admin/DeviceApiKeyManagement';
import BusinessRequestsManagement from '../components/admin/BusinessRequestsManagement';
import TrapRentalRequestsManagement from '../components/admin/TrapRentalRequestsManagement';
import SubscriptionPlansManagement from '../components/admin/SubscriptionPlansManagement';
import BusinessSubscriptionsManagement from '../components/admin/BusinessSubscriptionsManagement';
import ProductManagement from '../components/admin/ProductManagement';
import ActiveIngredientManagement from '../components/admin/ActiveIngredientManagement';
import ModeOfActionGroupManagement from '../components/admin/ModeOfActionGroupManagement';
import UnitManagement from '../components/admin/UnitManagement';
import ManufacturerManagement from '../components/admin/ManufacturerManagement';
import FormulationManagement from '../components/admin/FormulationManagement';
import GeneStackManagement from '../components/admin/GeneStackManagement';
import ProductGroupManagement from '../components/admin/ProductGroupManagement';
import DataImportManagement from '../components/admin/DataImportManagement';
import ProductCategoryManagement from '../components/admin/ProductCategoryManagement';
import OperationTypeManagement from '../components/admin/OperationTypeManagement';
import OperationMethodManagement from '../components/admin/OperationMethodManagement';
import ImportReportDataManagement from '../components/admin/ImportReportDataManagement';
import RegionManagement from '../components/admin/RegionManagement';
import RecordManagement from '../components/admin/RecordManagement';
import TimePeriodManagement from '../components/admin/TimePeriodManagement';
import ReferenceDataExportImport from '../components/admin/ReferenceDataExportImport';
import ProductAliasManagement from '../components/admin/ProductAliasManagement';
import ProductCategoryAliasManagement from '../components/admin/ProductCategoryAliasManagement';
import GeneStackAliasManagement from '../components/admin/GeneStackAliasManagement';
import ImportColumnMapManagement from '../components/admin/ImportColumnMapManagement';
import IrrigationTypeManagement from '../components/admin/IrrigationTypeManagement';
import IrrigationMethodManagement from '../components/admin/IrrigationMethodManagement';
import EnergySourceManagement from '../components/admin/EnergySourceManagement';
import WaterSourceManagement from '../components/admin/WaterSourceManagement';
import CropManagement from '../components/admin/CropManagement';
import FieldSeasonManagement from '../components/admin/FieldSeasonManagement';
import DataValidationManagement from '../components/admin/DataValidationManagement';
import ValidationDashboard from '../components/admin/ValidationDashboard';
import BenchmarkReport from '../components/admin/BenchmarkReport';
import ReportDataExportManagement from '../components/admin/ReportDataExportManagement';
import RegionAliasManagement from '../components/admin/RegionAliasManagement';
import BusinessAliasManagement from '../components/admin/BusinessAliasManagement';
import FarmAliasManagement from '../components/admin/FarmAliasManagement';
import FieldAliasManagement from '../components/admin/FieldAliasManagement';

type SectionKey =
  | 'businesses' | 'contacts' | 'users' | 'roles'
  | 'business-requests' | 'trap-rental-requests' | 'subscription-plans' | 'business-subscriptions'
  | 'ftp-credentials' | 'device-api-keys'
  | 'products' | 'active-ingredients' | 'mode-of-action-groups' | 'units' | 'manufacturers' | 'formulations' | 'product-categories' | 'gene-stacks' | 'product-groups' | 'reference-data-export-import'
  | 'product-aliases' | 'product-category-aliases' | 'gene-stack-aliases' | 'region-aliases'
  | 'data-import' | 'import-column-maps'
  | 'operation-types' | 'operation-methods' | 'seasons' | 'import-report-data' | 'regions' | 'records' | 'irrigation-types'
  | 'field-season-data' | 'irrigation-methods' | 'energy-sources' | 'water-sources' | 'crops' | 'data-validation' | 'validation-dashboard' | 'report-data-export'
  | 'business-aliases' | 'farm-aliases' | 'field-aliases'
  | 'benchmarking';

interface Section {
  key: SectionKey;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

interface Group {
  label: string;
  sections: Section[];
}

const groups: Group[] = [
  {
    label: 'Directory',
    sections: [
      { key: 'businesses', label: 'Business Management', icon: <BusinessIcon />, content: <BusinessManagement /> },
      { key: 'contacts', label: 'Contact Management', icon: <ContactsIcon />, content: <ContactManagement /> },
      { key: 'users', label: 'User Management', icon: <PeopleIcon />, content: <UserManagement /> },
      {
        key: 'roles',
        label: 'Role Management',
        icon: <SecurityIcon />,
        content: (
          <Box>
            <Typography variant="h6" gutterBottom>Role Management</Typography>
            <Typography variant="body1" color="text.secondary">
              Configure user roles and permissions. Features for role CRUD operations will be implemented here.
            </Typography>
          </Box>
        ),
      },
    ],
  },
  {
    label: 'Billing & Subscriptions',
    sections: [
      { key: 'business-requests', label: 'Business Requests', icon: <HowToRegIcon />, content: <BusinessRequestsManagement /> },
      { key: 'trap-rental-requests', label: 'Trap Rental Requests', icon: <PestControlIcon />, content: <TrapRentalRequestsManagement /> },
      { key: 'subscription-plans', label: 'Subscription Plans', icon: <ReceiptLongIcon />, content: <SubscriptionPlansManagement /> },
      { key: 'business-subscriptions', label: 'Business Subscriptions', icon: <PaymentsIcon />, content: <BusinessSubscriptionsManagement /> },
    ],
  },
  {
    label: 'Products',
    sections: [
      { key: 'active-ingredients', label: 'Active Ingredients', icon: <ScienceIcon />, content: <ActiveIngredientManagement /> },
      { key: 'mode-of-action-groups', label: 'Mode of Action Groups', icon: <ShieldIcon />, content: <ModeOfActionGroupManagement /> },
      { key: 'products', label: 'Products', icon: <InventoryIcon />, content: <ProductManagement /> },
      { key: 'product-categories', label: 'Categories', icon: <CategoryIcon />, content: <ProductCategoryManagement /> },
      { key: 'units', label: 'Units', icon: <StraightenIcon />, content: <UnitManagement /> },
      { key: 'manufacturers', label: 'Manufacturers', icon: <FactoryIcon />, content: <ManufacturerManagement /> },
      { key: 'formulations', label: 'Formulations', icon: <ScatterPlotIcon />, content: <FormulationManagement /> },
      { key: 'gene-stacks', label: 'Gene Stacks', icon: <BiotechIcon />, content: <GeneStackManagement /> },
      { key: 'product-groups', label: 'Product Groups', icon: <GroupWorkIcon />, content: <ProductGroupManagement /> },
      { key: 'product-aliases', label: 'Product Map', icon: <LinkIcon />, content: <ProductAliasManagement /> },
      { key: 'product-category-aliases', label: 'Category Map', icon: <LinkIcon />, content: <ProductCategoryAliasManagement /> },
      { key: 'gene-stack-aliases', label: 'Gene Stack Map', icon: <LinkIcon />, content: <GeneStackAliasManagement /> },
    ],
  },
  {
    label: 'Record Management',
    sections: [
      { key: 'data-import', label: 'Data Import', icon: <UploadFileIcon />, content: <DataImportManagement /> },
      { key: 'import-column-maps', label: 'Import Column Map', icon: <ViewColumnIcon />, content: <ImportColumnMapManagement /> },
      { key: 'field-season-data', label: 'Field Seasons', icon: <TableViewIcon />, content: <FieldSeasonManagement /> },
      { key: 'report-data-export', label: 'Report Data Export', icon: <SimCardDownloadIcon />, content: <ReportDataExportManagement /> },
      { key: 'operation-types', label: 'Operation Types', icon: <AgricultureIcon />, content: <OperationTypeManagement /> },
      { key: 'operation-methods', label: 'Operation Methods', icon: <BuildIcon />, content: <OperationMethodManagement /> },
      { key: 'irrigation-types', label: 'Irrigation Types', icon: <OpacityIcon />, content: <IrrigationTypeManagement /> },
      { key: 'irrigation-methods', label: 'Irrigation Methods', icon: <WaterDropIcon />, content: <IrrigationMethodManagement /> },
      { key: 'energy-sources', label: 'Pump Energy Sources', icon: <LocalGasStationIcon />, content: <EnergySourceManagement /> },
      { key: 'water-sources', label: 'Water Sources', icon: <WavesIcon />, content: <WaterSourceManagement /> },
      { key: 'crops', label: 'Crops', icon: <GrassIcon />, content: <CropManagement /> },
      { key: 'seasons', label: 'Time Periods', icon: <EventIcon />, content: <TimePeriodManagement /> },
      { key: 'import-report-data', label: 'Imported Report Data', icon: <AssignmentIcon />, content: <ImportReportDataManagement /> },
      { key: 'regions', label: 'Cotton Growing Regions', icon: <PublicIcon />, content: <RegionManagement /> },
      { key: 'region-aliases', label: 'Region Map', icon: <LinkIcon />, content: <RegionAliasManagement /> },
      { key: 'business-aliases', label: 'Business Map', icon: <LinkIcon />, content: <BusinessAliasManagement /> },
      { key: 'farm-aliases', label: 'Farm Map', icon: <LinkIcon />, content: <FarmAliasManagement /> },
      { key: 'field-aliases', label: 'Field Map', icon: <LinkIcon />, content: <FieldAliasManagement /> },
      { key: 'reference-data-export-import', label: 'Export / Import', icon: <ImportExportIcon />, content: <ReferenceDataExportImport /> },
    ],
  },
  {
    label: 'Reports',
    sections: [
      { key: 'records', label: 'Records', icon: <ListAltIcon />, content: <RecordManagement /> },
      { key: 'benchmarking', label: 'Benchmarking', icon: <LeaderboardIcon />, content: <BenchmarkReport /> },
    ],
  },
  {
    label: 'Validation',
    sections: [
      { key: 'validation-dashboard', label: 'Dashboard', icon: <InsightsIcon />, content: <ValidationDashboard /> },
      { key: 'data-validation', label: 'Data Validation', icon: <FactCheckIcon />, content: <DataValidationManagement /> },
    ],
  },
  {
    label: 'System',
    sections: [
      { key: 'ftp-credentials', label: 'FTP Credentials', icon: <KeyIcon />, content: <FtpCredentialManagement /> },
      { key: 'device-api-keys', label: 'Data API Keys', icon: <VpnKeyIcon />, content: <DeviceApiKeyManagement /> },
    ],
  },
];

const allSections: Section[] = groups.flatMap((g) => g.sections);

const Admin: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = auth.hasRole('DMO_ADMIN');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const activeKey: SectionKey = React.useMemo(() => {
    const match = allSections.find((s) => location.pathname.includes(s.key));
    return match?.key ?? 'businesses';
  }, [location.pathname]);

  const activeSection = allSections.find((s) => s.key === activeKey) ?? allSections[0];

  const activeGroupLabel = React.useMemo(
    () => groups.find((g) => g.sections.some((s) => s.key === activeKey))?.label ?? groups[0].label,
    [activeKey]
  );
  // Whichever group holds the active section starts expanded; the rest start
  // collapsed. Track expansion per-group label so more than one can be open.
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>(
    () => Object.fromEntries(groups.map((g) => [g.label, g.label === activeGroupLabel]))
  );

  const handleNavigate = (key: SectionKey) => {
    navigate(`/admin/${key}`);
    setMobileNavOpen(false);
  };

  const navList = (
    <Box sx={{ py: 0.5 }}>
      {groups.map((group) => (
        <Accordion
          key={group.label}
          disableGutters
          elevation={0}
          square
          expanded={expandedGroups[group.label] ?? false}
          onChange={(_, isExpanded) =>
            setExpandedGroups((prev) => ({ ...prev, [group.label]: isExpanded }))
          }
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography variant="subtitle2" color="text.secondary">{group.label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List component="nav" dense disablePadding>
              {group.sections.map((section) => (
                <ListItemButton
                  key={section.key}
                  selected={activeKey === section.key}
                  onClick={() => handleNavigate(section.key)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.label} />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <Container maxWidth={false} disableGutters={isMobile} sx={isMobile ? { px: 1 } : { px: 3 }}>
      <Box sx={{ mt: 0, mb: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 3 } }}>
          <AdminPanelSettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h4" component="h1">
            Administration
          </Typography>
        </Box>

        {!isAdmin ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="error">
              You need DMO_ADMIN to manage administration. Contact your administrator.
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box>
            <Button
              startIcon={<MenuIcon />}
              variant="outlined"
              onClick={() => setMobileNavOpen(true)}
              sx={{ mb: 1.5 }}
            >
              {activeSection.label}
            </Button>
            <Drawer anchor="left" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}>
              <Box sx={{ width: 280 }}>{navList}</Box>
            </Drawer>
            <Paper sx={{ p: 1.5 }}>
              {activeSection.content}
            </Paper>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Paper sx={{ width: 260, flexShrink: 0 }}>
              {navList}
            </Paper>

            <Paper sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
              {activeSection.content}
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Admin;
